import { MinimumRemainingEquityValidationError } from 'modules/business_rules/api/minimumRemainingEquity';
import {
  checkRegionAvailabilityForTio,
  minimumRemainingEquity,
  mortgageTermByAge,
  ltvForProduct,
  applicantDateOfBirth,
} from 'modules/business_rules/api';

import { ukPostalCode } from 'shared/regex/regex';
import { CheckRegionAvailabilityForTioError } from 'modules/business_rules/api/checkRegionAvailabilityForTio';
import { cachedRequest } from 'apiClient';
import { TestOptions } from 'yup';
import { MortgageTermByAgeValidationError } from 'modules/business_rules/api/mortgageTermByAge';
import { LTVValidationError } from 'modules/business_rules/api/ltv';
import { ApplicantFormType, FIELDS } from '../Form.types';
import { Option } from 'shared/interface/globalInterface';
import { ProductRepaymentType, ProductType, ProductTypes, ProductRepaymentTypes } from 'domain/Product';
import { Nullable } from 'utils/types';
import { format } from 'utils/date';
import { numberWithCommas } from 'utils/text';
import {
  ApplicantDateOfBirthValidationError,
  ValidationErrorReason,
} from 'modules/business_rules/api/applicantDateOfBirth';

export async function checkPostcode(postcode: string) {
  const result = await cachedRequest<{ error?: Error }>(async () => {
    try {
      await checkRegionAvailabilityForTio(postcode);
    } catch (e) {
      return {
        error: e,
      };
    }
    return {
      error: undefined,
    };
  }, `validate-postcode-${postcode}`);

  if (result.error) {
    throw result.error;
  }

  return result;
}

export async function checkMinimumRemainingEquity(loanAmountRequired: number, propertyValue: number, postcode: string) {
  const result = await cachedRequest<{ error?: Error }>(async () => {
    try {
      await minimumRemainingEquity(loanAmountRequired, propertyValue, postcode);
    } catch (e) {
      return {
        error: e,
      };
    }
    return {
      error: undefined,
    };
  }, `validate-minimum-remaining-equity-${loanAmountRequired}-${propertyValue}-${postcode}`);

  if (result.error) {
    throw result.error;
  }

  return result;
}

export const minimumRemainingEquityTestOptions = (products: Option<{ productType: string }>[]): TestOptions => ({
  name: 'Minimum remaining equity validation',
  test: async function() {
    const { loanRequested, propertyValue, postCode, mortgageProduct, productRepaymentType } = this.parent;
    const productType = products.find((product) => product.value === mortgageProduct)?.params?.productType;
    if (
      loanRequested &&
      propertyValue &&
      ukPostalCode.test(postCode) &&
      productType === ProductTypes.TIO &&
      productRepaymentType === ProductRepaymentTypes.INTEREST_ONLY
    ) {
      try {
        await checkPostcode(postCode);
        await checkMinimumRemainingEquity(loanRequested, Number(propertyValue), postCode);

        return true;
      } catch (error) {
        if (error instanceof MinimumRemainingEquityValidationError) {
          if (error.isWarningError()) {
            return true;
          }

          let message = error.message;
          if (error.params && error.params.minimumEquity && error.params.missingAmount) {
            message = `There needs to be at least £${numberWithCommas(
              error.params.minimumEquity,
            )} of equity remaining in the property. Please amend the property value or loan requested by £${numberWithCommas(
              error.params.missingAmount,
            )}.`;
          }

          return this.createError({
            path: `${FIELDS.LOAN_DETAILS_PREFIX}.${FIELDS.LOAN_REQUESTED}`,
            message: message,
          });
        } else if (error instanceof Error) {
          return this.createError({
            path: `${FIELDS.LOAN_DETAILS_PREFIX}.${FIELDS.LOAN_REQUESTED}`,
            message: error.message,
          });
        }
      }
    }

    return true;
  },
});

export const mortgageTermByAgeTestOptions = (
  productType: ProductType | undefined,
  applicant1: ApplicantFormType,
  applicant2: Nullable<ApplicantFormType>,
): TestOptions => ({
  name: 'Mortgage term by age validation',
  test: async function() {
    const { termLength, productRepaymentType } = this.parent;

    if (productType && applicant1.dateOfBirth && termLength) {
      try {
        await mortgageTermByAge.cachedValidate(
          productType,
          productRepaymentType,
          format(applicant1.dateOfBirth).fromViewToRequest(),
          applicant2 ? (applicant2.dateOfBirth ? format(applicant2.dateOfBirth).fromViewToRequest() : null) : null,
          termLength,
        );
      } catch (error) {
        if (error instanceof MortgageTermByAgeValidationError && error.params) {
          return this.createError({
            path: `${FIELDS.LOAN_DETAILS_PREFIX}.${FIELDS.TERM_LENGTH}`,
            message: `The applicant must not be over 80 years old at the end of term. Max term = ${
              error.params.maxTermLength
            } ${error.params.maxTermLength > 1 ? 'years' : 'year'}.`,
          });
        }
      }
    }

    return true;
  },
});

export const mortgageTermByAgeForApplicantValidation = async (
  productType: ProductType | undefined,
  productRepaymentType: Nullable<ProductRepaymentType>,
  applicant1: ApplicantFormType,
  applicant2: Nullable<ApplicantFormType>,
  termLength: Nullable<number>,
) => {
  if (productType && productRepaymentType && applicant1.dateOfBirth && termLength) {
    try {
      await mortgageTermByAge.cachedValidate(
        productType,
        productRepaymentType,
        format(applicant1.dateOfBirth).fromViewToRequest(),
        applicant2 ? (applicant2.dateOfBirth ? format(applicant2.dateOfBirth).fromViewToRequest() : null) : null,
        termLength,
      );
    } catch (error) {
      if (error instanceof MortgageTermByAgeValidationError) {
        return `The applicant must not be over 80 years old at the end of term. Max term = ${
          error.params.maxTermLength
        } ${error.params.maxTermLength > 1 ? 'years' : 'year'}.`;
      }
    }
  }

  return false;
};

export const postcodeTIORegionTestOptions: TestOptions = {
  name: 'Check region availability for TIO products',
  test: async function() {
    const { productSelectionPostcode } = this.parent;

    if (!productSelectionPostcode) {
      return true;
    }

    try {
      if (productSelectionPostcode && ukPostalCode.test(productSelectionPostcode)) {
        await checkPostcode(productSelectionPostcode);

        return true;
      }
    } catch (error) {
      if (error instanceof CheckRegionAvailabilityForTioError) {
        return this.createError({
          path: FIELDS.POST_CODE,
          message: error.message,
        });
      }
    }

    return true;
  },
};

export const ltvForProductTestOptions = (products: Option<{ productType: string; code: string }>[]): TestOptions => ({
  name: 'Validate LTV',
  test: async function() {
    const { loanRequested, propertyValue, mortgageProduct, productRepaymentType } = this.parent;
    const productType = products.find((product) => product.value === mortgageProduct)?.params?.productType;
    const productCode = products.find((product) => product.value === mortgageProduct)?.params?.code;

    if (loanRequested && propertyValue && productType && productCode) {
      try {
        await ltvForProduct.cachedValidate(
          productType,
          loanRequested,
          propertyValue,
          productCode as Nullable<ProductType>,
          productRepaymentType as Nullable<ProductRepaymentType>,
        );
      } catch (error) {
        if (error instanceof LTVValidationError) {
          return this.createError({
            path: `${FIELDS.LOAN_DETAILS_PREFIX}.${FIELDS.LOAN_REQUESTED}`,
            message: error.message,
          });
        }

        if (error instanceof Error) {
          return this.createError({
            path: `${FIELDS.LOAN_DETAILS_PREFIX}.${FIELDS.LOAN_REQUESTED}`,
            message: error.message,
          });
        }
      }
    }

    return true;
  },
});

export const fullAgeValidation = async (
  productType: Nullable<ProductType>,
  value: any,
  repaymentType: ProductRepaymentType,
) => {
  if (!productType || !value || !repaymentType) {
    return false;
  }
  try {
    await applicantDateOfBirth.cachedValidate(format(value).fromViewToRequest(), productType, repaymentType);
  } catch (error) {
    if (error instanceof ApplicantDateOfBirthValidationError) {
      if (error.getReason() === ValidationErrorReason.IN_ACCEPTABLE_RANGE) {
        return false;
      }

      let message =
        "We are only able to accept applications from over 50 year old's, as per FCA retirement interest only mortgage guidelines.";

      if (error.getReason() === ValidationErrorReason.TOO_OLD) {
        message = `Applicant(s) cannot be over ${error.getMaxAcceptableYears()} years of age when a term-end interest-only product is selected`;
      }
      if (error.getReason() === ValidationErrorReason.TOO_YOUNG) {
        message = `Applicant(s) cannot be below ${error.getInAcceptableYears()} years and ${error.getInAcceptableMonths()} months of age when a term-end interest-only product is selected`;
      }

      return message;
    }
  }
  return false;
};
